import { LoadedProps, RouteLoadProps, defRoute } from '@components/router';
import { FixedContent, FixedPage } from '@course/components/fixed-page';
import { AdminNav, AdminTabHeader, AdminTabWrapper } from '@course/components/admin-nav';
import { rpx } from 'client/lib/rpx-client';
import dayjs from 'dayjs';
import { useMemo, useState } from 'preact/hooks';
import {
  IcoExternalLink,
  IcoMapPin,
  IcoPresentation,
  IcoUsers,
  IcoVideoCamera,
} from '@components/icons';
import { Pill } from '@components/pill';
import { UserProfileIcon } from '@components/avatars/user-profile-icon';
import type { MeetingsAgendaItem } from 'server/rpx/endpoints/meetings-schedule';
import 'shared/dateutil/date-formats';

type RangeMode = 'upcoming' | 'past';

const WINDOW_DAYS = 60;

function buildRange(mode: RangeMode) {
  const now = dayjs();
  if (mode === 'upcoming') {
    return {
      startDate: now.subtract(2, 'hour').toISOString(),
      endDate: now.add(WINDOW_DAYS, 'day').endOf('day').toISOString(),
    };
  }
  return {
    startDate: now.subtract(WINDOW_DAYS, 'day').startOf('day').toISOString(),
    endDate: now.toISOString(),
  };
}

async function load(_route: RouteLoadProps) {
  const { startDate, endDate } = buildRange('upcoming');
  const items = await rpx.meetingsSchedule.getMeetingsSchedule({ startDate, endDate });
  return { items };
}

export const route = defRoute({
  load,
  authLevel: 'admin',
  Page,
});

type MeetingType = MeetingsAgendaItem['type'];

const typeLabels: Record<MeetingType, string> = {
  videoconference: 'Live session',
  zoom: 'Zoom',
  presentation: 'Presentation',
  external: 'External',
  inperson: 'In person',
};

function TypeIcon({ type }: { type: MeetingType }) {
  if (type === 'inperson') {
    return <IcoMapPin class="w-4 h-4 text-amber-600 shrink-0" />;
  }
  if (type === 'external') {
    return <IcoExternalLink class="w-4 h-4 text-sky-600 shrink-0" />;
  }
  if (type === 'presentation') {
    return <IcoPresentation class="w-4 h-4 text-violet-600 shrink-0" />;
  }
  return <IcoVideoCamera class="w-4 h-4 text-emerald-600 shrink-0" />;
}

function Page({ state, setState }: LoadedProps<typeof load>) {
  const [rangeMode, setRangeMode] = useState<RangeMode>('upcoming');
  const [courseFilter, setCourseFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<'all' | MeetingType>('all');
  const [issuesOnly, setIssuesOnly] = useState(false);
  const [isReloading, setIsReloading] = useState(false);

  const courses = useMemo(() => {
    const map = new Map<string, string>();
    for (const item of state.items) {
      map.set(item.courseId, item.courseTitle);
    }
    return Array.from(map.entries())
      .map(([id, title]) => ({ id, title }))
      .sort((a, b) => a.title.localeCompare(b.title));
  }, [state.items]);

  const issuesCount = useMemo(() => state.items.filter((i) => i.configIssue).length, [state.items]);

  const filtered = useMemo(() => {
    return state.items.filter((item) => {
      if (courseFilter !== 'all' && item.courseId !== courseFilter) {
        return false;
      }
      if (typeFilter !== 'all' && item.type !== typeFilter) {
        return false;
      }
      if (issuesOnly && !item.configIssue) {
        return false;
      }
      return true;
    });
  }, [state.items, courseFilter, typeFilter, issuesOnly]);

  async function changeRange(next: RangeMode) {
    if (next === rangeMode) {
      return;
    }
    setRangeMode(next);
    setIsReloading(true);
    try {
      const { startDate, endDate } = buildRange(next);
      const items = await rpx.meetingsSchedule.getMeetingsSchedule({ startDate, endDate });
      setState((prev) => ({ ...prev, items }));
    } finally {
      setIsReloading(false);
    }
  }

  return (
    <FixedPage>
      <FixedContent class="bg-white">
        <AdminNav currentPage="meetings" pageTitle="Call schedule" />
        <AdminTabWrapper>
          <AdminTabHeader title="Call schedule" />
          <p class="text-sm text-gray-500 -mt-6 mb-6">
            Live sessions and other meetings across every course in the tenant — past 2 hours
            through the next {WINDOW_DAYS} days.
          </p>
          <div class="flex flex-wrap gap-3 mb-6 items-center">
            <div class="inline-flex rounded border border-gray-300 overflow-hidden text-sm">
              <button
                type="button"
                class={`px-3 py-1 ${
                  rangeMode === 'upcoming' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-700'
                }`}
                onClick={() => changeRange('upcoming')}
              >
                Upcoming
              </button>
              <button
                type="button"
                class={`px-3 py-1 border-l border-gray-300 ${
                  rangeMode === 'past' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-700'
                }`}
                onClick={() => changeRange('past')}
              >
                Past
              </button>
            </div>
            <label class="flex items-center gap-2 text-sm">
              <span class="text-gray-700">Course</span>
              <select
                class="border border-gray-300 rounded px-2 py-1 text-sm bg-white"
                value={courseFilter}
                onChange={(e) => setCourseFilter((e.target as HTMLSelectElement).value)}
              >
                <option value="all">All courses</option>
                {courses.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.title}
                  </option>
                ))}
              </select>
            </label>
            <label class="flex items-center gap-2 text-sm">
              <span class="text-gray-700">Type</span>
              <select
                class="border border-gray-300 rounded px-2 py-1 text-sm bg-white"
                value={typeFilter}
                onChange={(e) =>
                  setTypeFilter((e.target as HTMLSelectElement).value as typeof typeFilter)
                }
              >
                <option value="all">All</option>
                <option value="videoconference">Live session</option>
                <option value="zoom">Zoom</option>
                <option value="presentation">Presentation</option>
                <option value="external">External</option>
                <option value="inperson">In person</option>
              </select>
            </label>
            {issuesCount > 0 && (
              <label class="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={issuesOnly}
                  onChange={(e) => setIssuesOnly((e.target as HTMLInputElement).checked)}
                />
                Needs attention ({issuesCount})
              </label>
            )}
            <span class="text-xs text-gray-500 ml-auto">
              {isReloading
                ? 'Loading…'
                : `${filtered.length.toLocaleString()} meeting${filtered.length === 1 ? '' : 's'}`}
            </span>
          </div>
          <MeetingsAgenda items={filtered} rangeMode={rangeMode} />
        </AdminTabWrapper>
      </FixedContent>
    </FixedPage>
  );
}

function MeetingsAgenda({
  items,
  rangeMode,
}: {
  items: MeetingsAgendaItem[];
  rangeMode: RangeMode;
}) {
  if (items.length === 0) {
    return (
      <div class="rounded-lg border border-dashed border-gray-300 p-12 text-center text-gray-500">
        {rangeMode === 'upcoming'
          ? 'No upcoming meetings match these filters.'
          : 'No past meetings match these filters.'}
      </div>
    );
  }

  const groups = groupByDay(items, rangeMode);

  return (
    <div class="flex flex-col gap-8">
      {groups.map((group) => (
        <section key={group.dayKey}>
          <h3 class="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
            {group.label}
          </h3>
          <div class="rounded-lg border border-gray-200 divide-y divide-gray-100 bg-white overflow-visible">
            {group.items.map((item) => (
              <AgendaRow key={item.id} item={item} />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

function groupByDay(items: MeetingsAgendaItem[], rangeMode: RangeMode) {
  const map = new Map<string, MeetingsAgendaItem[]>();
  for (const item of items) {
    const dayKey = dayjs.utc(item.scheduledAt).format('YYYY-MM-DD');
    const list = map.get(dayKey) ?? [];
    list.push(item);
    map.set(dayKey, list);
  }
  const today = dayjs.utc().format('YYYY-MM-DD');
  const tomorrow = dayjs.utc().add(1, 'day').format('YYYY-MM-DD');
  const entries = Array.from(map.entries()).sort(([a], [b]) =>
    rangeMode === 'past' ? b.localeCompare(a) : a.localeCompare(b),
  );
  return entries.map(([dayKey, dayItems]) => {
    let label = dayjs(dayKey).format('dddd, MMMM D');
    if (dayKey === today) {
      label = `Today · ${label}`;
    } else if (dayKey === tomorrow) {
      label = `Tomorrow · ${label}`;
    }
    const sortedItems =
      rangeMode === 'past'
        ? [...dayItems].sort((a, b) => (a.scheduledAt < b.scheduledAt ? 1 : -1))
        : dayItems;
    return { dayKey, label, items: sortedItems };
  });
}

function AgendaRow({ item }: { item: MeetingsAgendaItem }) {
  const isPast = dayjs.utc(item.scheduledAt).isBefore(dayjs.utc());
  const headcount = isPast
    ? item.attendedCount > 0
      ? { label: `${item.attendedCount} attended` }
      : undefined
    : item.rsvpEnabled && item.rsvpCount > 0
      ? { label: `${item.rsvpCount} going` }
      : undefined;
  return (
    <a
      href={item.editUrl}
      class={`group/row relative grid grid-cols-[7rem_1fr_auto] md:grid-cols-[8rem_10rem_1fr_auto] gap-4 items-center px-4 py-3 hover:bg-gray-50 ${
        isPast ? 'opacity-60' : ''
      }`}
    >
      <div class="flex flex-col">
        <span class="text-sm font-medium text-gray-900 tabular-nums">
          {dayjs.utc(item.scheduledAt).format('h:mm A')}
        </span>
        <span class="text-xs text-gray-500">{item.durationMinutes} min</span>
      </div>

      <div class="hidden md:flex items-center gap-2 min-w-0">
        <TypeIcon type={item.type} />
        <span class="text-xs text-gray-600 truncate">{typeLabels[item.type]}</span>
      </div>

      <div class="flex flex-col min-w-0">
        <div class="flex items-center gap-2 min-w-0">
          <span class="md:hidden">
            <TypeIcon type={item.type} />
          </span>
          <span class="text-sm font-medium text-gray-900 truncate">
            {item.title || 'Untitled meeting'}
          </span>
        </div>
        <div class="flex items-center gap-3 text-xs text-gray-500 mt-0.5">
          <span class="truncate max-w-[20rem]" title={item.courseTitle}>
            {item.courseTitle}
          </span>
          {item.guideName && (
            <span class="inline-flex items-center gap-1.5 min-w-0">
              <UserProfileIcon
                user={{ name: item.guideName, profilePhotoUrl: item.guidePhotoUrl }}
                size="size-5"
                maxWidth={48}
              />
              <span class="truncate">{item.guideName}</span>
            </span>
          )}
          {headcount && (
            <span class="inline-flex items-center gap-1">
              <IcoUsers class="w-3 h-3" />
              {headcount.label}
            </span>
          )}
        </div>
      </div>

      <div class="flex items-center justify-end shrink-0">
        {item.configIssue && (
          <Pill color="red">
            {item.configIssue === 'missingZoomId' ? 'Missing Zoom ID' : 'Missing join URL'}
          </Pill>
        )}
      </div>

      <MeetingPreviewCard item={item} isPast={isPast} />
    </a>
  );
}

function MeetingPreviewCard({ item, isPast }: { item: MeetingsAgendaItem; isPast: boolean }) {
  return (
    <div class="hidden md:block absolute left-0 right-0 top-full mt-2 z-30 invisible opacity-0 group-hover/row:visible group-hover/row:opacity-100 group-hover/row:delay-300 transition-opacity duration-150 pointer-events-none group-hover/row:pointer-events-auto">
      <div class="rounded-lg border border-gray-200 bg-white shadow-xl p-4 max-w-3xl">
        <div class="flex items-baseline justify-between gap-4 mb-2">
          <div class="flex items-center gap-2 min-w-0">
            <TypeIcon type={item.type} />
            <span class="text-base font-semibold text-gray-900 truncate">
              {item.title || 'Untitled meeting'}
            </span>
          </div>
          <span class="text-xs text-gray-500 shrink-0 tabular-nums">
            {dayjs.utc(item.scheduledAt).format('ddd, MMM D · h:mm A')} · {item.durationMinutes} min
          </span>
        </div>

        <div class="text-xs text-gray-600 mb-3 flex items-center gap-3 flex-wrap">
          <span>{item.courseTitle}</span>
          {item.guideName && <span>· {item.guideName}</span>}
          {item.recordMeeting && <span>· Recording on</span>}
        </div>

        {item.descriptionExcerpt && (
          <p class="text-sm text-gray-700 leading-relaxed mb-3 whitespace-pre-wrap">
            {item.descriptionExcerpt}
          </p>
        )}

        <dl class="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1 text-xs text-gray-700">
          {item.type === 'zoom' && (
            <>
              <dt class="text-gray-500">Zoom ID</dt>
              <dd class="font-mono break-all">{item.zoomMeetingId || '—'}</dd>
            </>
          )}
          {item.type === 'external' && (
            <>
              <dt class="text-gray-500">Join URL</dt>
              <dd class="break-all">{item.externalJoinUrl || '—'}</dd>
            </>
          )}
          {item.type === 'inperson' && (
            <>
              <dt class="text-gray-500">Location</dt>
              <dd>{item.inpersonLocation || '—'}</dd>
            </>
          )}

          <dt class="text-gray-500">Reminders</dt>
          <dd>{describeReminders(item)}</dd>

          {item.rsvpEnabled && (
            <>
              <dt class="text-gray-500">{isPast ? 'Attendance' : 'RSVPs'}</dt>
              <dd>
                {isPast
                  ? `${item.attendedCount} attended of ${item.rsvpCount} RSVP${item.rsvpCount === 1 ? '' : 's'}`
                  : `${item.rsvpCount} going`}
              </dd>
            </>
          )}
          {!item.rsvpEnabled && isPast && item.attendedCount > 0 && (
            <>
              <dt class="text-gray-500">Attendance</dt>
              <dd>{item.attendedCount} attended</dd>
            </>
          )}
        </dl>
      </div>
    </div>
  );
}

function describeReminders(item: MeetingsAgendaItem): string {
  const parts: string[] = [];
  if (item.firstReminderEnabled) {
    const mins = item.firstReminderOffsetMinutes;
    parts.push(mins ? `${formatOffset(mins)} before` : '1st on');
  }
  if (item.secondReminderEnabled) {
    const mins = item.secondReminderOffsetMinutes;
    parts.push(mins ? `${formatOffset(mins)} before` : '2nd on');
  }
  return parts.length > 0 ? parts.join(' · ') : 'Off';
}

function formatOffset(minutes: number): string {
  if (minutes >= 1440 && minutes % 1440 === 0) {
    const days = minutes / 1440;
    return `${days} day${days === 1 ? '' : 's'}`;
  }
  if (minutes >= 60 && minutes % 60 === 0) {
    const hours = minutes / 60;
    return `${hours} hr${hours === 1 ? '' : 's'}`;
  }
  return `${minutes} min`;
}
